import { Link, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useData } from '@yingbaba/api';
import type { WorkspaceTaskPage } from '@yingbaba/types';
import { Empty, PageTitle, Pagination, QueryState } from '@yingbaba/ui';
import { date } from '@yingbaba/utils';
import { ArrowRight } from '@yingbaba/icons';

export const taskFilters = [
  { value: 'pending', label: '待处理' },
  { value: 'overdue', label: '已逾期' },
  { value: 'completed', label: '已完成' },
  { value: 'all', label: '全部记录' },
];
export function MyTasks({ compact = false }: { compact?: boolean }) {
  const [params, setParams] = useSearchParams();
  const status = compact
    ? 'pending'
    : taskFilters.find((f) => f.value === params.get('status'))?.value || 'pending';
  const page = compact ? 1 : Math.max(1, Number(params.get('page')) || 1),
    size = compact ? 5 : 20;
  const q = useData<WorkspaceTaskPage>(
    `/workspace/my-tasks?status=${status}&page=${page}&page_size=${size}`,
  );
  useEffect(() => {
    if (!compact && q.data) {
      const last = Math.max(1, Math.ceil(q.data.total / size));
      if (page > last)
        setParams(
          (p) => {
            p.set('page', String(last));
            return p;
          },
          { replace: true },
        );
    }
  }, [q.data, page, compact, size, setParams]);
  const summary = q.data?.summary;
  return (
    <>
      {!compact && (
        <PageTitle title="我的待办" description="集中查看当前组织中分配给你的项目任务。" />
      )}
      <section className="panel workspace-tasks">
        {compact && (
          <div className="panel-title">
            <h3>我的待办</h3>
            <Link to="/tasks" className="row">
              查看全部
              <ArrowRight size={15} />
            </Link>
          </div>
        )}
        <QueryState query={q}>
          {summary && (
            <div className="workspace-task-summary" aria-label="我的任务概况">
              <Link to="/tasks?status=pending">
                <strong>{summary.pending}</strong>
                <span>待处理</span>
              </Link>
              <Link to="/tasks?status=overdue" className={summary.overdue ? 'has-overdue' : ''}>
                <strong>{summary.overdue}</strong>
                <span>已逾期</span>
              </Link>
              <Link to="/tasks?status=completed">
                <strong>{summary.completed}</strong>
                <span>已完成</span>
              </Link>
            </div>
          )}
          {!compact && (
            <div className="tabs" role="tablist" aria-label="我的任务状态">
              {taskFilters.map((f) => (
                <button
                  type="button"
                  key={f.value}
                  role="tab"
                  aria-selected={status === f.value}
                  className={status === f.value ? 'active' : ''}
                  onClick={() => setParams(f.value === 'pending' ? {} : { status: f.value })}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {!q.data?.items.length && (
            <Empty
              title={
                status === 'pending'
                  ? '当前没有待办任务'
                  : status === 'overdue'
                    ? '当前没有逾期任务'
                    : '没有匹配的任务记录'
              }
              description={
                status === 'pending'
                  ? '项目中分配给你的任务，会集中显示在这里。'
                  : '可切换其他状态查看。'
              }
            />
          )}
          {q.data?.items.map((t) => (
            <Link
              key={t.id}
              className="list-item workspace-task"
              to={`/projects/${t.project_id}?tab=tasks&task=${t.id}`}
            >
              <div className="grow">
                <strong>{t.title}</strong>
                <p>
                  {t.project_title}
                  <span> · {t.due_date ? `截止 ${date(t.due_date)}` : '未设截止日期'}</span>
                </p>
              </div>
              <span className={`tag ${t.overdue ? 'task-overdue' : ''}`}>
                {t.project_status !== 'active'
                  ? t.project_status === 'completed'
                    ? '项目已归档'
                    : '项目已关闭'
                  : t.status === 'completed'
                    ? '已完成'
                    : t.overdue
                      ? '已逾期'
                      : '待处理'}
              </span>
              <ArrowRight size={15} />
            </Link>
          ))}
        </QueryState>
        {!compact && (
          <Pagination
            page={page}
            size={20}
            total={q.data?.total || 0}
            onPage={(v) =>
              setParams((p) => {
                p.set('page', String(v));
                return p;
              })
            }
          />
        )}
      </section>
    </>
  );
}
